import React from "react";
import styled from "styled-components";
import { Form, Formik, FormikHelpers } from "formik";
import * as Sentry from "@sentry/react";
import * as Yup from "yup";
import Recruiter from "../../apiResources/Recruiter";
import Employer from "../../apiResources/Employer";
import RecruiterUser from "../../apiResources/RecruiterUser";
import EmployerUser from "../../apiResources/EmployerUser";
import TextLink from "../library/TextLink";
import { TextInputField } from "../library/Form";
import SubmitButton from "../library/Form/SubmitButton";
import SettingLabel from "./SettingLabel";
import { addHttpsPrefix } from "../../helpers";

const WebsiteAddressSettingsOption = (props: Props) => {
  const {
    user,
    company,
    isEditing = false,
    disabled = false,
    onEditStart,
    onEditComplete,
    noCancel,
  } = props;

  const save = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    const website = addHttpsPrefix(values.websiteAddress);

    if (website === company.websiteAddress) {
      onEditComplete?.();
      return;
    }

    try {
      company.assignAttributes({ websiteAddress: website });
      const success = await company.save();
      if (success) {
        onEditComplete?.();
      } else {
        formik.setErrors({
          websiteAddress: company.errors.websiteAddress?.fullMessage,
        });
      }
    } catch (err) {
      Sentry.captureException(err);
      formik.setErrors({
        websiteAddress: "Uh oh, something went wrong there",
      });
    } finally {
      company.rollback();
    }
  };

  const edit = () => {
    if (!disabled) {
      onEditStart();
    }
  };

  if (!isEditing) {
    return (
      <section>
        <Header>
          <SettingLabel>Website Address</SettingLabel>
          {user.isAdmin && (
            <TextLink disabled={disabled} onClick={edit}>
              Edit
            </TextLink>
          )}
        </Header>
        {company.websiteAddress ? (
          <div>{company.websiteAddress}</div>
        ) : (
          <div>Not specified</div>
        )}
      </section>
    );
  }

  const initialValues: FormValues = {
    websiteAddress: company.websiteAddress
      ? company.websiteAddress.replace(/https?:\/\//, "")
      : "",
  };

  const validation = Yup.object({
    websiteAddress: Yup.string().required("Please enter a website address"),
  });

  return (
    <section>
      <Formik
        initialValues={initialValues}
        onSubmit={save}
        validationSchema={validation}
      >
        <Form autoComplete="off">
          <Header>
            <SettingLabel>Website Address</SettingLabel>
            {!noCancel && <TextLink onClick={onEditComplete}>Cancel</TextLink>}
          </Header>
          <TextInputField
            prefixDisplay="https://"
            label="Website Address"
            hiddenLabel
            name="websiteAddress"
          />
          <SubmitButton />
        </Form>
      </Formik>
    </section>
  );
};

type FormValues = {
  websiteAddress: string;
};

type Props = {
  user: RecruiterUser | EmployerUser;
  company: Recruiter | Employer;
  isEditing?: boolean;
  editable?: boolean;
  disabled?: boolean;
  onEditStart?: () => void;
  onEditComplete?: () => void;
  noCancel?: boolean;
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

export default WebsiteAddressSettingsOption;
